import { CheckCircleOutlined, Close } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { Match, Show, Switch } from '@side/pantry';
import { useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import {
  BuyerAgreementsExpiringSoon,
  Carousel,
  CommissionFormsAwaitingAction,
  ComplianceReviewIncomplete,
  EnvelopesNeedReminders,
  ListingAgreementsExpiringSoon,
} from '@/views/Home/components/NeedsAttention/components';
import { SectionContainer } from '@/views/Home/components/SectionContainer';
import { useNeedsAttention } from '@/views/Home/hooks';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { LoadingSkeleton } from './components/LoadingSkeleton';

export const NEEDS_ATTENTION_IS_DISMISSED =
  'HOME_PAGE__NEEDS_ATTENTION__isDismissed';
const numberFormatter = new Intl.NumberFormat('en-US');

function SkeletonChip() {
  return (
    <Skeleton
      data-testid="NeedsAttention__SkeletonChip"
      height={22}
      sx={{ borderRadius: '16px' }}
      width={26}
      variant="rounded"
    />
  );
}

export function NeedsAttention() {
  const trackEvent = useTrackHomePageAnalytics();
  const [isDismissed, setIsDismissed] = useState(
    localStorage.getItem(NEEDS_ATTENTION_IS_DISMISSED) === 'true',
  );

  const {
    commissionForm,
    listingAgreements,
    buyerAgreements,
    complianceReview,
    envelopes,
    loading,
  } = useNeedsAttention();

  const [forceLoadingState, setForceLoadingState] = useState(loading);
  const isLoading = forceLoadingState || loading;

  useEffectOnce(() => {
    setTimeout(() => {
      setForceLoadingState(false);
    }, 2000);
  });

  const aggregateNeedsAttentionCount: number = [
    commissionForm?.interactionCount ?? 0,
    complianceReview?.interactionCount ?? 0,
    listingAgreements?.interactionCount ?? 0,
    envelopes?.envelopeCount ?? 0,
    buyerAgreements?.interactionCount ?? 0,
  ].reduce((sum, currCount = 0) => sum + currCount, 0);

  function toggleSectionVisibility() {
    setIsDismissed((prevState) => {
      localStorage.setItem(NEEDS_ATTENTION_IS_DISMISSED, `${!prevState}`);

      return !prevState;
    });
  }

  function clearDismissedStateIfResultsExist() {
    if (aggregateNeedsAttentionCount && isDismissed && !loading) {
      toggleSectionVisibility();
    }
  }

  useEffect(clearDismissedStateIfResultsExist, [
    aggregateNeedsAttentionCount,
    isDismissed,
    loading,
  ]);

  const showNeedsAttnSection = (loading && !isDismissed) || !isDismissed;
  const attentionItemsMayExist = loading || aggregateNeedsAttentionCount;
  const thereAreZeroAttnItems = !loading && aggregateNeedsAttentionCount === 0;

  useEffect(() => {
    if (thereAreZeroAttnItems && !isDismissed) {
      trackEvent(TrackHomePageEvent.NeedsAttentionEmptyMessageMount);
    }
  });

  const carouselItems = useMemo(
    () =>
      [
        Boolean(commissionForm?.interactionCount) && (
          <CommissionFormsAwaitingAction
            key="CommissionFormsAwaitingAction"
            commissions={commissionForm?.commissions ?? []}
            interactionCount={commissionForm?.interactionCount ?? 0}
            singleCommissionFormUrl={commissionForm?.singleCommissionFormUrl}
          />
        ),

        Boolean(complianceReview?.interactionCount) && (
          <ComplianceReviewIncomplete
            key="ComplianceReviewIncomplete"
            audits={complianceReview?.audits ?? []}
            interactionCount={complianceReview?.interactionCount ?? 0}
          />
        ),

        Boolean(listingAgreements?.interactionCount) && (
          <ListingAgreementsExpiringSoon
            key="ListingAgreementsExpiringSoon"
            interactions={listingAgreements?.interactions ?? []}
            interactionCount={listingAgreements?.interactionCount ?? 0}
          />
        ),

        Boolean(buyerAgreements?.interactionCount) && (
          <BuyerAgreementsExpiringSoon
            key="BuyerAgreementsExpiringSoon"
            interactions={buyerAgreements?.interactions ?? []}
            interactionCount={buyerAgreements?.interactionCount ?? 0}
          />
        ),

        Boolean(envelopes?.interactionCount) && (
          <EnvelopesNeedReminders
            key="EnvelopesNeedReminders"
            envelopeCount={envelopes?.envelopeCount ?? 0}
            interactionCount={envelopes?.interactionCount ?? 0}
            interactions={envelopes?.interactions ?? []}
          />
        ),
      ].filter(Boolean),
    [
      commissionForm,
      complianceReview,
      listingAgreements,
      buyerAgreements,
      envelopes,
    ],
  );

  return (
    <Box data-testid="txm-home-needs-attention-section">
      <Collapse in={showNeedsAttnSection} unmountOnExit>
        <SectionContainer>
          <Typography
            component="h2"
            sx={{
              alignItems: 'center',
              color: 'other.spaceCadet',
              display: 'flex',
              gap: 0.5,
            }}
            variant="h4"
          >
            Needs Attention
            <Show fallback={<SkeletonChip />} when={!isLoading}>
              <Chip
                data-testid="NeedsAttention__AggregateCountChip"
                label={numberFormatter.format(aggregateNeedsAttentionCount)}
                size="xs"
                sx={{
                  backgroundColor: aggregateNeedsAttentionCount
                    ? 'warning.main'
                    : 'action.hover',
                  color: aggregateNeedsAttentionCount
                    ? 'text.primary'
                    : 'action.active',
                }}
              />
            </Show>
          </Typography>

          <Switch>
            <Match when={isLoading}>
              <LoadingSkeleton />
            </Match>

            <Match when={attentionItemsMayExist}>
              <Carousel items={carouselItems} />
            </Match>

            <Match when={thereAreZeroAttnItems}>
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: 'background.default',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  justifyContent: 'space-between',
                  marginTop: 2,
                  paddingX: 3,
                  paddingY: 4.5,
                  position: 'relative',
                }}
              >
                <IconButton
                  aria-label="Dismiss the Needs Attention Section"
                  onClick={() => {
                    toggleSectionVisibility();
                    trackEvent(
                      TrackHomePageEvent.NeedsAttentionEmptyMessageDismissed,
                    );
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                >
                  <Close color="primary" />
                </IconButton>

                <CheckCircleOutlined color="success" height={20} width={20} />

                <Typography component="p" variant="h5">
                  You&apos;re all caught up!
                </Typography>

                <Typography sx={{ textAlign: 'center' }} variant="body2">
                  Time sensitive tasks that need your attention will show up
                  here
                </Typography>
              </Box>
            </Match>
          </Switch>
        </SectionContainer>
      </Collapse>
    </Box>
  );
}
